import {
	WEBVIEW_TEMPLATE_IDS,
	WEBVIEW_TEMPLATES,
	resolveWebviewPreviewUrl,
	type WebviewTemplateId,
} from '@/entities/webview-templates';
import { Switch } from '@/components/ui/switch';
import { getApiOrigin } from '@/lib/api-origin';
import { useMemo, useState } from 'react';
import WebviewCustomUrlInput from './webview-custom-url-input';
import WebviewPhonePreview from './webview-phone-preview';
import WebviewTemplateList from './webview-template-list';

type WebviewSettingsSectionProps = {
	enabled: boolean;
	webviewType: WebviewTemplateId | null;
	url: string | null;
	onEnabledChange: (value: boolean) => void;
	onWebviewTypeChange: (value: WebviewTemplateId) => void;
	onUrlChange: (value: string) => void;
	disabled?: boolean;
};

export default function WebviewSettingsSection({
	enabled,
	webviewType,
	url,
	onEnabledChange,
	onWebviewTypeChange,
	onUrlChange,
	disabled,
}: WebviewSettingsSectionProps) {
	const apiOrigin = useMemo(() => getApiOrigin(import.meta.env.VITE_BACK_URL), []);
	const [customPreviewUrl, setCustomPreviewUrl] = useState<string | null>(null);

	const templates = WEBVIEW_TEMPLATE_IDS.map(id => ({
		id,
		label: WEBVIEW_TEMPLATES[id].label,
		description: WEBVIEW_TEMPLATES[id].description,
		available: WEBVIEW_TEMPLATES[id].available,
	}));

	const autoPreviewUrl = useMemo(
		() => resolveWebviewPreviewUrl(webviewType, url, apiOrigin),
		[webviewType, url, apiOrigin],
	);

	const previewUrl = webviewType === 'custom' ? customPreviewUrl : autoPreviewUrl;

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex items-center justify-between gap-3'>
				<div className='min-w-0'>
					<h3 className='text-base font-semibold'>Настройки WebView</h3>
					<p className='text-sm text-muted-foreground'>
						{enabled
							? 'Выберите вид WebView и заполните шаблон'
							: 'WebView отключён — при открытии APK будет стандартный экран'}
					</p>
				</div>
				<Switch checked={enabled} disabled={disabled} onCheckedChange={onEnabledChange} />
			</div>

			{enabled ? (
				<>
					<div className='grid gap-4 lg:grid-cols-[minmax(0,280px)_1fr] lg:items-start'>
						<WebviewPhonePreview
							previewUrl={previewUrl}
							iframeBlockedHint={
								webviewType === 'custom'
									? 'Некоторые сайты блокируют встраивание в iframe — в APK превью может отличаться.'
									: undefined
							}
						/>
						<WebviewTemplateList
							value={webviewType}
							onChange={id => {
								onWebviewTypeChange(id);
								if (id !== 'custom') {
									setCustomPreviewUrl(null);
								}
							}}
							disabled={disabled}
							templates={templates}
						/>
					</div>

					{webviewType === 'custom' ? (
						<WebviewCustomUrlInput
							value={url ?? ''}
							onChange={onUrlChange}
							disabled={disabled}
							onPreview={() => setCustomPreviewUrl(resolveWebviewPreviewUrl('custom', url, apiOrigin))}
						/>
					) : null}
				</>
			) : null}
		</div>
	);
}
